import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import api from '../services/api';
import type {
  PermissionCatalogueEntry,
  RoleCatalogueEntry,
  RolePermissions,
} from '../types/userManagement.types';

// PRX-116 T4: the role list, labels and permission keys come from the API
// (GET /roles, GET /permissions); see types/userManagement.types.ts.
export type { CustomRoleValue, RolePermissions } from '../types/userManagement.types';

export interface PharmacyUser {
  phoxUserId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  ocp?: string;
  phoxadminRole?: string;
  /** null: no active role at this pharmacy (never a Viewer default). */
  customRole?: string | null;
  permissions?: RolePermissions | null;
  /** null when there is no role row: no Active/Inactive status to show. */
  isActive?: boolean | null;
  lastSyncedAt?: Date;
  assignedAt?: Date;
}

export interface UserActivity {
  id: string;
  phoxUserId: string;
  pharmacyId?: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  details?: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: Date;
}

/** One entry of GET /roles: role, label, description and live permissions. */
export type AvailableRole = RoleCatalogueEntry;

interface UserManagementState {
  // Users
  users: PharmacyUser[];
  selectedUser: PharmacyUser | null;
  userActivities: UserActivity[];
  
  // Roles and the permission catalogue (both from the backend)
  availableRoles: AvailableRole[];
  permissionCatalogue: PermissionCatalogueEntry[];
  
  // Loading states
  loading: boolean;
  loadingUser: boolean;
  loadingActivities: boolean;
  
  // Error states
  error: string | null;
  
  // Selected pharmacy context
  selectedPharmacyId: string | null;
  
  // Actions
  fetchPharmacyUsers: (pharmacyId: string) => Promise<void>;
  fetchUserDetails: (userId: string, pharmacyId?: string) => Promise<void>;
  fetchUserActivities: (userId: string, pharmacyId: string, limit?: number) => Promise<void>;
  fetchAvailableRoles: () => Promise<void>;
  /** Load the role and permission catalogue unless it is loaded or loading: one request for any number of callers. */
  ensureRoleCatalogue: () => Promise<void>;
  setSelectedUser: (user: PharmacyUser | null) => void;
  setSelectedPharmacy: (pharmacyId: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  users: [],
  selectedUser: null,
  userActivities: [],
  availableRoles: [],
  permissionCatalogue: [],
  loading: false,
  loadingUser: false,
  loadingActivities: false,
  error: null,
  selectedPharmacyId: null,
};

// The one in-flight catalogue request, shared by every ensureRoleCatalogue caller.
let roleCatalogueRequest: Promise<void> | null = null;

const useUserManagementStore = create<UserManagementState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchPharmacyUsers: async (pharmacyId: string) => {
        set({ loading: true, error: null });
        try {
          const response = await api.get(`/api/user-management/pharmacy/${pharmacyId}/users`);
          set({ 
            users: response.data,
            selectedPharmacyId: pharmacyId,
            loading: false,
          });
        } catch (error: any) {
          console.error('Failed to fetch pharmacy users:', error);
          set({ 
            error: error.response?.data?.message || 'Failed to fetch pharmacy users',
            loading: false,
          });
        }
      },

      fetchUserDetails: async (userId: string, pharmacyId?: string) => {
        set({ loadingUser: true, error: null });
        try {
          const params = pharmacyId ? { pharmacyId } : {};
          const response = await api.get(`/api/user-management/user/${userId}`, { params });
          set({ 
            selectedUser: response.data,
            loadingUser: false,
          });
        } catch (error: any) {
          console.error('Failed to fetch user details:', error);
          set({ 
            error: error.response?.data?.message || 'Failed to fetch user details',
            loadingUser: false,
          });
        }
      },

      fetchUserActivities: async (userId: string, pharmacyId: string, limit = 100) => {
        set({ loadingActivities: true, error: null });
        try {
          // PRX-116 T1: activity is read at the path pharmacy only.
          const response = await api.get(
            `/api/user-management/pharmacy/${pharmacyId}/users/${userId}/activities`,
            { params: { limit } },
          );
          set({ 
            userActivities: response.data,
            loadingActivities: false,
          });
        } catch (error: any) {
          console.error('Failed to fetch user activities:', error);
          set({ 
            error: error.response?.data?.message || 'Failed to fetch user activities',
            loadingActivities: false,
          });
        }
      },

      ensureRoleCatalogue: () => {
        const { availableRoles, permissionCatalogue } = get();
        if (availableRoles.length > 0 && permissionCatalogue.length > 0) return Promise.resolve();
        if (!roleCatalogueRequest) {
          roleCatalogueRequest = get()
            .fetchAvailableRoles()
            .finally(() => {
              roleCatalogueRequest = null;
            });
        }
        return roleCatalogueRequest;
      },

      fetchAvailableRoles: async () => {
        try {
          const [roles, permissions] = await Promise.all([
            api.get('/api/user-management/roles'),
            api.get('/api/user-management/permissions'),
          ]);
          set({ availableRoles: roles.data, permissionCatalogue: permissions.data });
        } catch (error: any) {
          console.error('Failed to fetch available roles:', error);
          set({ 
            error: error.response?.data?.message || 'Failed to fetch available roles',
          });
        }
      },

      setSelectedUser: (user: PharmacyUser | null) => {
        set({ selectedUser: user });
      },

      setSelectedPharmacy: (pharmacyId: string | null) => {
        set({ selectedPharmacyId: pharmacyId });
        
        // Clear users when pharmacy changes
        if (pharmacyId !== get().selectedPharmacyId) {
          set({ users: [], selectedUser: null, userActivities: [] });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'user-management-store',
    }
  )
);

export default useUserManagementStore;